import { inject, Injectable, InjectionToken } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ExaminationForm } from '@pixacare/domain';
import { mapToArray } from '@shared/rx-pipes/map-to-array.pipe';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { first, map, withLatestFrom } from 'rxjs/operators';
import { ExaminationDisplaysByExaminationFormId } from '../models/examination-displays-by-examination-form-id';
import { ExaminationNavigationConfig } from '../models/examination-navigation-config';
import { ExaminationsService } from './examinations.service';

export const EXAMINATION_NAVIGATION_CONFIG = new InjectionToken<ExaminationNavigationConfig>(
  'EXAMINATION_NAVIGATION_CONFIG',
);

@UntilDestroy()
@Injectable()
export class ExaminationsNavigationService {
  private readonly config = inject(EXAMINATION_NAVIGATION_CONFIG, { optional: true });

  private readonly examinationsService = inject(ExaminationsService);
  private readonly examinationFormId = new ReplaySubject<number>(1);
  private readonly examinationIndex = new BehaviorSubject<number>(0);

  readonly index$ = this.examinationIndex.asObservable();

  readonly examinationForm$ = combineLatest([this.examinationsService.examinationForms$, this.examinationFormId]).pipe(
    map(([examinationForms, examinationFormId]) => examinationForms[examinationFormId]),
  );

  readonly examinations$ = combineLatest([this.examinationsService.examinationsState$, this.examinationFormId]).pipe(
    map(([examinationsState, examinationFormId]) => examinationsState.data[examinationFormId] ?? []),
  );

  readonly selectedExamination$ = combineLatest([this.examinations$, this.index$]).pipe(
    map(([examinations, index]) => (examinations ? examinations[index] : null)),
  );

  get canGoToPrevious$(): Observable<boolean> {
    return this.index$.pipe(map((index) => index > 0));
  }

  get canGoToNext$(): Observable<boolean> {
    return combineLatest([this.examinations$, this.index$]).pipe(
      map(([examinations, index]) => index < examinations.length - 1),
    );
  }

  constructor() {
    this.initSelection();

    // subscribe to examination creation to go to last examination
    this.examinationsService.examinationCreated$.pipe(untilDestroyed(this)).subscribe(() => {
      this.goToLast();
    });

    // protection to avoid getting out of bounds
    this.examinations$.pipe(withLatestFrom(this.index$), untilDestroyed(this)).subscribe(([examinations, index]) => {
      if (index >= examinations.length) {
        this.goToLast();
      }
    });
  }

  selectExaminationForm(examinationFormId: number): void {
    this.examinationFormId.next(examinationFormId);
    this.goToLast();
  }

  setIndex(index: number): void {
    this.examinationIndex.next(index);
  }

  goToLast(): void {
    combineLatest([this.examinations$, this.index$])
      .pipe(first())
      .subscribe(([examinations, index]) => {
        if (index !== examinations.length - 1) {
          this.setIndex(examinations.length - 1);
        }
      });
  }

  goToId(id: number): void {
    this.examinations$.pipe(first()).subscribe((examinations) => {
      const index = examinations.findIndex((examination) => examination.id === id);
      this.setIndex(index);
    });
  }

  goToPrevious(): void {
    this.index$.pipe(first()).subscribe((index) => {
      if (index !== 0) {
        this.setIndex(index - 1);
      }
    });
  }

  goToNext(): void {
    combineLatest([this.examinations$, this.index$])
      .pipe(first())
      .subscribe(([examinations, index]) => {
        if (index !== examinations.length - 1) {
          this.setIndex(index + 1);
        }
      });
  }

  private initSelection(): void {
    if (this.config?.examinationFormId) {
      this.examinationFormId.next(this.config.examinationFormId);
    }

    // wait until data is loaded to select default selection
    combineLatest([
      this.examinationsService.examinationForms$.pipe(mapToArray()),
      this.examinationsService.examinationsState$,
    ])
      .pipe(first(([examinationForms, examinationsState]) => examinationForms.length && examinationsState.isSuccess))
      .subscribe(([examinationForms, examinationsState]) => {
        if (!this.config?.examinationFormId) {
          const examinationForm = this.selectPreferedExaminationForm(examinationForms, examinationsState.data);
          this.examinationFormId.next(examinationForm.id);
        }

        if (this.config?.examinationId) {
          this.goToId(this.config.examinationId);
        } else {
          this.goToLast();
        }
      });
  }

  private selectPreferedExaminationForm(
    examinationForms: ExaminationForm[],
    examinationsByFormId: ExaminationDisplaysByExaminationFormId,
  ): ExaminationForm {
    if (!examinationForms?.length) {
      return null;
    }

    const sortedExaminationForms = examinationForms.sort((a, b) =>
      a.isGlobal === b.isGlobal ? 0 : a.isGlobal ? 1 : -1,
    );

    return (
      sortedExaminationForms.find((examinationForm) => examinationsByFormId[examinationForm.id]?.length > 0) ||
      sortedExaminationForms[0]
    );
  }
}
